<template>
    <b-card>
        <change-orders-driver
            ref="bulkDriverModalRef"
            :orders="selectedOrders"
            @success="bulkDriverSuccess()"
        />

        <b-card-header>
            <b-container>
                <b-row>
                    <b-col md="6" cols="12" v-if="showCustomer">
                        <customer-type-ahead v-model="filters.customer_id" />
                    </b-col>
                    <b-col>
                        <b-form-input
                            v-model="query"
                            placeholder="Activities Search..."
                            type="search"
                        ></b-form-input>
                    </b-col>
                </b-row>

                <b-row class="mt-4">
                    <b-col md="2" cols="12">
                        <b-button-group>
                            <b-button
                                variant="light"
                                size="sm"
                                @click="shouldShowFilters = !shouldShowFilters"
                            >
                                <span style="white-space: nowrap">
                                    Filters
                                    <b-icon
                                        :icon="`chevron-${
                                            shouldShowFilters ? 'up' : 'down'
                                        }`"
                                    ></b-icon>
                                </span>
                            </b-button>
                            <b-button
                                variant="light"
                                size="sm"
                                @click="resetFilters"
                                v-b-tooltip.hover
                                title="Reset Filters"
                            >
                                <b-icon
                                    icon="arrow-repeat"
                                    variant="dark"
                                ></b-icon>
                            </b-button>
                        </b-button-group>
                    </b-col>
                    <b-col md="5" cols="12">
                        <order-date-range v-model="dateRangeIdx" />
                    </b-col>

                    <b-col md="5" cols="12">
                        <page-actions
                            :buttons-actions="buttonActions"
                            :dropdown-actions="otherActions"
                            dropdown-title="Bulk Actions"
                            control-class="float-right"
                            control-size="sm"
                        />
                    </b-col>
                </b-row>

                <query-filter
                    class="mt-12"
                    :date-range="dateRangeIdx"
                    v-show="shouldShowFilters"
                    ref="queryFilter"
                    v-model="filters"
                    :roles="roles"
                    :order-types="orderTypes"
                    :categories="categories"
                    :checkoff-types="checkoffTypes"
                    :officers="officers"
                    :disabled-officers="!hasPermissionView"
                />
            </b-container>
        </b-card-header>

        <b-card-body>
            <b-tabs class="mt-3" align="center" v-model="entityIdx">
                <b-tab
                    v-for="item in entityOptions"
                    :key="'entity' + item.value"
                    :title="item.text"
                    :active="item.text == CustomerEntity"
                ></b-tab>
            </b-tabs>
            <p class="mt-3">
                Page: {{ pagination.page }} of {{ pageCount }} Total:
                {{ pagination.total }}
            </p>

            <b-table
                :items="orders"
                :fields="fields"
                :busy="tableLoading"
                small
                striped
                selectable
                responsive
                @row-selected="onRowSelected"
                ref="selectableTable"
            >
                <template #table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                </template>
                <!-- A custom formatted header cell for field 'name' -->
                <template #head(check)>
                    <b-form-checkbox
                        v-if="orders.length > 0"
                        :checked="orders.length === selectedOrders.length"
                        @change="toggleAllRow()"
                    ></b-form-checkbox>
                </template>

                <template #cell(check)="{ rowSelected, index }">
                    <b-form-checkbox
                        :checked="rowSelected"
                        @change="toggleRow(index, rowSelected)"
                    ></b-form-checkbox>
                </template>

                <template #cell(actions)="{ item }">
                    <b-button
                        size="sm"
                        :to="`/admin/activities/${item.id}`"
                        class="mr-2"
                        variant="primary"
                    >
                        View
                    </b-button>
                </template>

                <template #cell(id)="{ item }">
                    <h6>
                        <b-badge :variant="item | filterStatusVariant">{{
                            item | filterStatusName
                        }}</b-badge>
                    </h6>
                </template>

                <template #cell(custom_total)="{ value }">
                    <h6 class="pr-2" style="float: right">
                        {{ value | formatPrice }}
                    </h6>
                </template>

                <template #cell(tasks)="{ item }">
                    <h6>
                        <b-badge variant="info">
                            {{ item.completedTasks }} / {{ item.totalTasks }}
                        </b-badge>
                    </h6>
                </template>
            </b-table>

            <b-row>
                <b-col>
                    <b-pagination
                        class="float-right"
                        v-model="pagination.page"
                        :total-rows="pagination.total"
                        :per-page="pagination.pageSize"
                        aria-controls="customerTable"
                        pills
                        align="right"
                    ></b-pagination>
                </b-col>
            </b-row>
        </b-card-body>
    </b-card>
</template>
<script lang="js">
import CustomerTypeAhead from '@/components/CustomerTypeAhead.vue'
import popupMixins from '@/mixins/popupMixins'
import permissionsMixins from '@/mixins/permissionsMixins'

import moment from 'moment'
import _ from 'lodash'

//import autoTable from 'jspdf-autotable'
//import html2canvas from 'html2canvas'
import QueryFilter from './QueryFilter.vue'
import ChangeOrdersDriver from './ChangeOrdersDriver.vue'
import OrderDateRange from '@/components/OrderDateRange.vue'
import PageActions from '@/components/PageActions.vue'
import locationMixins from '@/mixins/locationMixins'

const CustomerEntity = 'Customer'
const CustomerEntityIndex = 1

const defaultFilters = Object.freeze({
    driver_id: null,
    completion_date_from: null,
    completion_date_to: null,
    checkoff_type_id: null,
    completed: null,
    checkoff_completed: null,
    type: null,
    status: null,
    role_id: null,
    request_category_id: null,
    is_updated: null,
    due_from: null, // moment().startOf('month').format('YYYY-MM-DD'),
    due_to: null, // '9999-01-01', // moment().endOf('month').format('YYYY-MM-DD'),
})

const dateRangeEnum = {
    today: 0,
    yesterday: 1,
    custom: 2,
}

export default {
    name: 'orders-table',
    components: {
        CustomerTypeAhead,
        OrderDateRange,
        QueryFilter,
        ChangeOrdersDriver,
        PageActions,
    },
    mixins: [popupMixins, permissionsMixins,locationMixins],
    props: {
        type: {
            type: String,
        },
        title: String,
    },
    data() {
        return {
            CustomerEntity,
            entityIdx: CustomerEntityIndex,
            query: '',
            shouldShowFilters: false,
            filters: { ...defaultFilters },
            bulkAction: 'none',

            dateRangeIdx: 2,
            selectedOrders: [],
            csvFields: {
                ID: 'id',
                'Reference ID': 'reference_id',
                'Customer Name': 'customer.name',
            },

            tableLoading: false,

            fields: [
                'check',
                'actions',
                {
                    key: 'reference_no',
                    label: 'Ref.No',
                },
                {
                    key: 'schedule_at',
                    label: 'Due',
                    formatter: value => (value || '').split(' ')[0],
                },
                {
                    key: 'completed_at',
                    label: 'Completed',
                    formatter: value => (value || '').split(' ')[0],
                },
                {
                    key: 'id',
                    label: 'Status',
                },

                'description',
                {
                    key: 'type',
                    label: 'Type',
                    formatter: value => value.name,
                },

                {
                    key: 'driver',
                    label: 'Officer',
                    formatter: value => {
                        const { first_name, last_name } = value || {}
                        return `${first_name || ''} ${last_name || ''}`
                    },
                },
                {
                    key: 'custom_total',
                    label: 'Total',
                    formatter: (value, key, item) =>
                        // console.log("item.......................................",item),
                        item.items.reduce((_total, item) => {
                            const cost = item.quantity * item.price
                            const tax_cost = (cost * item.tax_rate) / 100
                            _total += cost + tax_cost
                            return _total
                        }, 0),
                },
                'tasks',
            ],
        }
    },

    filters: {
        formatPrice: function (value) {
            let val = (value / 1).toFixed(2).replace(',', '.')
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        },
        capitalize: function (value) {
            if (!value) return ''
            value = value.toString()
            return value.charAt(0).toUpperCase() + value.slice(1)
        },
        filterStatusName(item) {
            return item.completed
                ? 'Completed'
                : item.released
                    ? 'Processing'
                    : 'Pending'
        },

        filterStatusVariant(item) {
            return item.completed
                ? 'success'
                : item.released
                    ? 'primary'
                    : 'danger'
        },
    },

    computed: {
        buttonActions() {
            const actions = []
            if (this.hasPermissionCreate) {
                actions.push({
                    action: () => this.createNewOrder(),
                    icon: 'plus-circle',
                    variant: 'primary',
                    text: 'Create New Activity',
                    size: 'lg',
                })
            }
            return actions
        },
        otherActions() {
            const actions = []
            if (this.hasPermissionCreate) {
                actions.push({
                    action: () => this.importOrders(),
                    text: 'Import Activity',
                })
            }

            //bulk actions
            const anyDisabled = !(
                this.selectedOrders.length > 0 &&
                (this.hasPermissionUpdate || this.hasPermissionDelete)
            )
            const updateDisabled = !(
                this.selectedOrders.length > 0 && this.hasPermissionUpdate
            )
            const deleteDisabled = !(
                this.selectedOrders.length > 0 && this.hasPermissionDelete
            )

            actions.push({
                divider: true,
                text: 'Bulk Actions',
                disabled: anyDisabled,
            })

            actions.push(
                {
                    action: () => this.bulkSetDriver(),
                    text: 'Set Officer',
                    disabled: updateDisabled,
                },
                {
                    action: () => this.bulkSetIncomplete(),
                    text: 'Set Incomplete',
                    disabled: updateDisabled,
                },
                {
                    action: () => this.bulkSetcomplete(),
                    text: 'Mark As Complete',
                    disabled: updateDisabled,
                },
                {
                    action: () => this.bulkRelease(),
                    text: 'Release Activities',
                    disabled: updateDisabled,
                }
            )

            actions.push({
                action: () => this.bulkDelete(),
                text: 'Delete',
                disabled: deleteDisabled,
            })

            return actions
        },

        showCustomer() {
            return this.fields.some(x => x.key == 'customer')
        },
        efields() {
            const _fields = {
                ID: 'id',
                'Ref No': 'reference_no',
                'Created By': 'created_by.first_name',
                'Activity Type': 'type.name',
                Customer: 'customer.name',
                Tax: 'tax',
                'Total Price': 'total_price',
                'Customer Signature': {
                    field: 'signature',
                    callback: value => (value === 1 ? 'YES' : 'NO'),
                },
            }
            if (this.entityIdx == CustomerEntityIndex) return _fields
            delete _fields.Customer
            delete _fields['Customer Signature']

            return _fields
        },
        entityOptions() {
            return this.$store.getters['transactionType/entityOptions']
        },

        extended_order_items() {
            return this.orders.reduce((_items, _order) => {
                _items = [..._items, ..._order.items]
                _items = _items.map(x => {
                    x.order = _order
                    x.total_price = x.quantity * x.price
                    x.tax = (x.total_price * x.tax_rate) / 100
                    return x
                })
                return _items
            }, [])
        },

        pageCount() {
            return Math.ceil(this.pagination.total / this.pagination.pageSize)
        },
        orderTypes() {
            return this.$store.state.orderType.items
        },

        orders() {
            return this.$store.state.orders.items.map(x => {
                // console.log("...................x...........",x)
                x.driver = x.driver || {}
                x.customer = x.customer || {}
                x.warehouse = x.warehouse || {}
                return x
            })
        },
        pagination() {
            return this.$store.state.orders.pagination || {}
        },
        dateObject() {
            return {
                today: {
                    due_from: moment().format('YYYY-MM-DD'),
                    due_to: moment().format('YYYY-MM-DD'),
                },
                yesterday: {
                    due_from: moment().subtract(1, 'days').format('YYYY-MM-DD'),
                    due_to: moment().subtract(1, 'days').format('YYYY-MM-DD'),
                },
                custom: {
                    due_from: '2020-01-01',
                    due_to: moment().format('YYYY-MM-DD'),
                },
            }
        },
        user() {
            const _user = this.$store.state.user
            console.log("user-----------", _user)
            return _user
        },
        officers() {
            return this.$store.getters['users/sorted_officers']
        },
        roles() {
            return this.$store.state.roles.items
        },
        categories() {
            return this.$store.state.requestCategory.categories
        },

        checkoffTypes() {
            const types = this.$store.state.checkoffTypes.types
            console.log('types', types)
            return types
        },
    },
    watch: {
        hasPermissionView: function (val) {
            console.log("user-----------", this.user)
            console.log("hasPermissionView--------", val)
        },
        query: _.debounce(function (query) {
            this.$router.push({ query: { query } })
            this.doSearch()
        }, 200),
        entityIdx: function (val) {
            this.checkCustomerField(val)
        },
        'pagination.page': function () {
            this.clearSelected()
            this.doSearch()
        },
        orders: {
            handler: function () {
                this.tableLoading = false
            },
            deep: true,
        },

        dateRangeIdx: function (val) {
            switch (val) {
                case dateRangeEnum.today:
                    this.$set(
                        this.filters,
                        'due_from',
                        moment().format('YYYY-MM-DD')
                    )
                    this.$set(
                        this.filters,
                        'due_to',
                        moment().format('YYYY-MM-DD')
                    )
                    break
                //due_to: moment().endOf('month').format('YYYY-MM-DD'),
                case dateRangeEnum.yesterday:
                    this.$set(
                        this.filters,
                        'due_from',
                        moment().subtract(1, 'days').format('YYYY-MM-DD')
                    )
                    this.$set(
                        this.filters,
                        'due_to',
                        moment().subtract(1, 'days').format('YYYY-MM-DD')
                    )
                    break
                default:
                    break
            }
            this.clearSelected()
        },
        filters: {
            handler: function ({ checkoff_type_id, role_id, request_category_id, due_from, due_to }) {
                if (
                    due_from === this.dateObject.today.due_from &&
                    due_to === this.dateObject.today.due_to
                ) {
                    this.dateRangeIdx = 0
                } else if (
                    due_from === this.dateObject.yesterday.due_from &&
                    due_to === this.dateObject.yesterday.due_to
                ) {
                    this.dateRangeIdx = 1
                } else {
                    this.dateRangeIdx = 2
                }
                this.checkCheckoffField(checkoff_type_id)

                this.checkCategoryField(request_category_id)
                this.checkRoleField(role_id)
                this.doSearch()
            },
            deep: true,
        },
    },

    mounted() {

        //http://localhost:8080/#/orders?page=1&query=&changed=0&driver_id=0&customer_id=0&warehouse_id=0&completed=any&type=any&is_updated=any&due_from=2020-01-01&due_to=2020-09-16

        /*  const { entityOptions } = this
        const entity = entityOptions[this.entityIdx].value
        this.filters.entity = this.$route.query.entity || entity */

        const { query } = this.$route
        console.log({ query })

        if (query.query) this.query = query.query
        if (query.changed) this.$set(this.filters, 'changed', query.changed)

        if (query.driver_id)
            this.$set(this.filters, 'driver_id', parseInt(query.driver_id))
        if (query.customer_id)
            this.$set(this.filters, 'customer_id', parseInt(query.customer_id))

        if (query.completed)
            this.$set(this.filters, 'completed', query.completed)
        if (query.type) this.$set(this.filters, 'type', query.type)
        if (query.is_updated)
            this.$set(this.filters, 'is_updated', query.is_updated)
        if (query.due_from) this.$set(this.filters, 'due_from', query.due_from)
        if (query.due_to) this.$set(this.filters, 'due_to', query.due_to)

        const { entityIdx } = this
        this.checkCustomerField(entityIdx)

        console.log("user-----------", this.user)
        console.log("hasPermissionView--------", this.hasPermissionView)

        if (!this.hasPermissionView) {
            this.$set(this.filters, 'driver_id', this.user.id)
        }
    },
    methods: {
        createNewOrder() {
            this.$router.push('/admin/activities/0')
        },
        importOrders() {
            this.$emit('import')
        },
        resetFilters() {
            this.entityIdx = CustomerEntityIndex
            let driver_id = null;
            if (!this.hasPermissionView) {
                driver_id = this.user.id
            }
            this.filters = { ...defaultFilters, driver_id }
            this.doSearch()
        },
        checkCustomerField(_entity) {
            const { fields, entityOptions } = this
            const customerField = {
                key: 'customer',
                label: 'Customer',
                formatter: value => value.name,
            }
            const addCustomerField = () => {
                this.fields.splice(5, 0, customerField)
            }
            const deleteCustomerField = idx => {
                this.fields.splice(idx, 1)
            }
            const isValid = _idx => _idx >= 0
            const isCustomerEntity = () =>
                entityOptions[_entity].text == 'Customer'

            const idx = fields.findIndex(x => x.key === customerField.key)
            if (!isValid(_entity) || entityOptions.length == 0) return

            if (!isValid(idx) && isCustomerEntity()) addCustomerField()
            else if (isValid(idx) && !isCustomerEntity())
                deleteCustomerField(idx)

            this.doSearch(_entity)
        },

        checkCheckoffField(checkoff_type_id) {
            const { fields, checkoffTypes } = this
            console.log('checkoffTypes', checkoffTypes)
            const checkoffType = checkoffTypes.find(
                x => x.id === checkoff_type_id
            )
            const checkoffField = {
                key: 'checkoffs',
                label: (checkoffType && checkoffType.name) || 'Reviewed',
                formatter: value => {
                    const isCompleted =
                        (
                            value.find(
                                x => x.checkoff_type_id == checkoff_type_id
                            ) || {}
                        ).completed == 1
                    return isCompleted ? 'Yes' : 'No'
                },
            }
            const addCheckoffField = () =>
                this.fields.splice(6, 0, checkoffField)

            const deleteCheckoffField = idx => this.fields.splice(idx, 1)

            const updateCheckoffField = idx =>
                this.fields.splice(idx, 1, checkoffField)

            const isValid = _idx => _idx >= 0

            const idx = fields.findIndex(x => x.key === checkoffField.key)

            if (!isValid(idx) && checkoff_type_id) addCheckoffField()
            else if (isValid(idx) && checkoff_type_id) updateCheckoffField(idx)
            else if (!checkoff_type_id && isValid(idx)) deleteCheckoffField(idx)
        },

        checkCategoryField(request_category_id) {
            const { fields } = this

            const CategoryField = {
                key: 'request_category_id',
                label: 'Activity Category',
                formatter: (value, key, item) =>
                    item.category.name,
            }
            const addCategoryField = () =>
                this.fields.splice(6, 0, CategoryField)

            const deleteCategoryField = idx => this.fields.splice(idx, 1)

            const updateCategoryField = idx =>
                this.fields.splice(idx, 1, CategoryField)

            const isValid = _idx => _idx > 0

            const idx = fields.findIndex(x => x.key === CategoryField.key)

            if (!isValid(idx) && request_category_id) addCategoryField()
            else if (isValid(idx) && request_category_id) updateCategoryField(idx)
            else if (!request_category_id && isValid(idx)) deleteCategoryField(idx)


        },
        checkRoleField(role_id) {
            const { fields } = this

            const RoleField = {
                key: 'role_id',
                label: 'Role',
                formatter: (value, key, item) =>
                     item.driver.roles && item.driver.roles[0].role && item.driver.roles[0].role.name,
            }
            const addRoleField = () =>
                this.fields.splice(7, 0, RoleField)

            const deleteRoleField = idx => this.fields.splice(idx, 1)

            const updateRoleField = idx =>
                this.fields.splice(idx, 1, RoleField)

            const isValid = _idx => _idx > 0

            const idx = fields.findIndex(x => x.key === RoleField.key)

            if (!isValid(idx) && role_id) addRoleField()
            else if (isValid(idx) && role_id) updateRoleField(idx)
            else if (!role_id && isValid(idx)) deleteRoleField(idx)


        },
        onRowSelected(items) {
            this.selectedOrders = items
        },
        selectAllRows() {
            this.$refs.selectableTable &&
                this.$refs.selectableTable.selectAllRows()
        },
        clearSelected() {
            this.$refs.selectableTable &&
                this.$refs.selectableTable.clearSelected()
        },
        toggleRow(index, rowSelected) {
            // Rows are indexed from 0, so the third row is index 2
            if (rowSelected) this.$refs.selectableTable.unselectRow(index)
            else this.$refs.selectableTable.selectRow(index)
        },

        toggleAllRow() {
            if (this.orders.length === this.selectedOrders.length)
                this.$refs.selectableTable.clearSelected()
            else this.$refs.selectableTable.selectAllRows()
        },

        ////////////////////////////////

        checkKeyType(key) {
            switch (key) {
                case 'created_at':
                case 'updated_at':
                case 'schedule_at':
                case 'completed_at':
                    return 'date'
                case 'completed':
                case 'completed_by':
                    return 'bool'
                case 'driver_id':
                case 'description':
                case 'address':
                case 'signature':
                case 'released':
                case 'warehouse_signee':
                case 'signee':
                case 'reference_no':
                case 'total_price':
                default:
                    return null
            }
        },

        bulkSetDriver() {
            this.$refs.bulkDriverModalRef.show()
        },
        bulkDriverSuccess() {
            this.doSearch()
            this.clearSelected()
        },
        bulkRelease() {
            const title = 'Mark Activities as Released?'
            const message = `Are you sure you want to Release these activities?`
            this.confirmMessage(title, message).then(result => {
                if (result) {
                    const orders = this.selectedOrders.map(x => x.id)
                    console.log('orders--tests', orders)
                    this.$store
                        .dispatch('orders/bulkRelease', { orders })
                        .then(() => {
                            this.doToast(
                                'Success!',
                                'Selected activities were Released',
                                'success'
                            )
                            this.clearSelected()
                            this.doSearch()
                        })
                        .catch(() => {
                            this.doToast(
                                'Error!',
                                'There was an Error Releasing Activities',
                                'danger'
                            )
                        })
                }
            })
        },

        bulkSetIncomplete() {
            const title = 'Mark Orders as Incomplete?'
            const message = `Include the Unrelease of Activities?`

            this.optionsMessage(title, message).then(results => {
                console.log('results', results)
                const { confirmed, optionSelected: unrelease } = results
                console.log('results', results)
                if (confirmed) {
                    const orders = this.selectedOrders.map(x => x.id)
                    this.$store
                        .dispatch('orders/bulkSetIncomplete', {
                            orders,
                            unrelease,
                        })
                        .then(() => {
                            this.doToast(
                                'Success!',
                                'Selected activities were marked as Incomplete',
                                'success'
                            )
                            this.clearSelected()
                            this.doSearch()
                        })
                        .catch(() => {
                            this.doToast(
                                'Error!',
                                'There was an error marking activities as incomplete',
                                'danger'
                            )
                        })
                }
            })
        },
        async bulkSetcomplete() {
            const title = 'Mark as Complete?'
            const message = `Are you sure you want to mark this activities as complete?`
            const result = await this.confirmMessage(title, message)
            if (!result) return
            try{
                this.tableLoading = true
                const address = await this.getSignatureAddress()
                const orders = this.selectedOrders.map(x => x.id)
                await this.$store
                    .dispatch('orders/bulkSetcomplete', {
                        orders, address
                    })

                this.doToast(
                    'Success!',
                    'Selected activities were marked as complete',
                    'success'
                )
                this.clearSelected()
                this.doSearch()
        }catch(error){
            this.doToast(
                    'Error Marking as Complete!',
                    error.message,
                    'danger'
                )
        }finally{
             this.tableLoading = false
        }
        },
        bulkDelete() {
            const title = 'Delete Activities?'
            const message = `Are you sure you want to delete these Activities`

            this.confirmMessage(title, message).then(result => {
                console.log('result', result)
                if (result) {
                    this.$store
                        .dispatch('orders/bulkDelete', {
                            orders: this.selectedOrders,
                        })
                        .then(() => {
                            this.doToast(
                                'Success!',
                                'Selected activities were deleted',
                                'success'
                            )

                            this.doSearch()
                        })
                        .catch(err => {
                            console.error(err)
                            this.doToast(
                                'Error!',
                                'Error deleting selected orders',
                                'danger'
                            )
                        })

                    this.clearSelected()
                }
            })
        },

        applyBulkAction() { },

        doSearch(entityIdx) {
            let {
                query,
                filters,
                pagination: { page },
                entityOptions,
            } = this

            page = parseInt(page) || 1
            entityIdx = entityIdx || this.entityIdx

            console.log({ entityOptions, entityIdx })

            if (entityOptions.length == 0) return

            filters.entity = entityOptions[entityIdx].value
            console.log('filters', filters)

            this.tableLoading = true
            this.$router.push({
                query: { page, query, ...filters },
            })
            this.$store.dispatch('orders/fetch', {
                query,
                filters,
                page,
            })
        },

        currency(num) {
            return num //.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        },
    },
}
</script>
<style></style>
